import { useCallback, useEffect, useMemo, useState, type VoidFunctionComponent } from 'react';
import { DropdownButton, MenuItem, type SelectCallback } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import useRevisionScans from '../../../../hooks/api/revisions/useRevisionScans';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { formatTimestamp } from '../../../../utils';
import { Icon, icons } from '../../../generic';
import VisibilityWrapper from '../../../generic/DynamicShowMoreList/VisibilityWrapper';
import FromNow from '../../../generic/FromNow';
import Loader from '../../../generic/Loader';
import { useRevisionScanIdQueryParam } from '../../hooks/useRevisionScanIdQueryParam';

import styles from './ProjectSummaryV2IssueScanDropdown.module.less';

type Props = {
  revisionLocator: string;
};

const getScannedAtLabel = (shouldShowAngleDown: boolean, scannedAt: string) => {
  return (
    <span className={styles.scanned}>
      <span>
        <span>Scanned </span>
        <span className={styles.scannedDate}><FromNow date={scannedAt} /></span>
      </span>
      {shouldShowAngleDown && <Icon icon={icons.angleDown} />}
    </span>
  );
};

const getMissingScanLabel = (shouldShowAngleDown: boolean) => {
  return (
    <span className={styles.scanned}>
      <span>
        <span>Scan date </span>
        <span className={styles.scannedDate}>unknown</span>
      </span>
      {shouldShowAngleDown && <Icon icon={icons.angleDown} />}
    </span>
  );
};

const ProjectSummaryV2IssueScanDropdown: VoidFunctionComponent<Props> = ({ revisionLocator }) => {
  const [isLastScanVisible, setIsLastScanVisible] = useState(false);

  const dispatch = useDispatch();
  const {
    revisionScanId: selectedRevisionScanId,
    setRevisionScanId: setSelectedRevisionScanId,
  } = useRevisionScanIdQueryParam();

  const {
    data: revisionScanPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useRevisionScans(revisionLocator, 50);

  const totalRevisionScans = revisionScanPages?.pages?.at(0)?.totalCount ?? 0;
  const hasMultipleRevisionScans = totalRevisionScans > 1;

  const revisionScans = revisionScanPages?.pages?.flatMap((revisionScansPage) => revisionScansPage.results);
  const selectedRevisionScan = revisionScans?.find((revisionScan) => revisionScan.id === selectedRevisionScanId);

  // Try and find a scan matching the selected one
  useEffect(() => {
    if (
      selectedRevisionScanId &&
      !selectedRevisionScan &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    selectedRevisionScanId,
    selectedRevisionScan,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // If the last scan in the list is visible AND there is another page, fetch the next one
  useEffect(() => {
    if (isLastScanVisible && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isLastScanVisible, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const revisionScanItems = useMemo(() => {
    if (!revisionScans) {
      return [];
    }
    const lastRevisionScanIndex = revisionScans.length - 1;

    return revisionScans.map((revisionScan, index) => {
      const shouldRenderLastItem = index === lastRevisionScanIndex;

      if (shouldRenderLastItem) {
        return (
          <MenuItem
            active={revisionScan.id === selectedRevisionScanId}
            eventKey={revisionScan.id}
            key={revisionScan.id}
          >
            <VisibilityWrapper onElementVisible={setIsLastScanVisible}>
              {formatTimestamp(revisionScan.scanned_at)}
            </VisibilityWrapper>
          </MenuItem>
        );
      }

      return (
        <MenuItem
          active={revisionScan.id === selectedRevisionScanId}
          eventKey={revisionScan.id}
          key={revisionScan.id}
        >
          {formatTimestamp(revisionScan.scanned_at)}
        </MenuItem>
      );
    });
  }, [selectedRevisionScanId, revisionScans]);

  const onSelect = useCallback((nextRevisionScanId?: number) => {
    if (nextRevisionScanId) {
      dispatch(IssuesV2Store.actions.setScanId(nextRevisionScanId));
      //  Add it to the URL in a query param.
      setSelectedRevisionScanId(nextRevisionScanId);
    }
  }, [dispatch, setSelectedRevisionScanId]);

  if (!selectedRevisionScan && isFetching) {
    return (
      <div className="container">
        <Loader size={20} />
      </div>
    );
  }

  let scanLabel = getMissingScanLabel(hasMultipleRevisionScans);
  if (selectedRevisionScan?.scanned_at) {
    scanLabel = getScannedAtLabel(hasMultipleRevisionScans, selectedRevisionScan.scanned_at);
  }

  if (hasMultipleRevisionScans) {
    return (
      <DropdownButton
        id="project-summary-v2-issue-scan-dropdown"
        className="dropdown-mid choose-scan"
        pullRight
        noCaret
        title={scanLabel}
        onSelect={onSelect as SelectCallback}
        >
        {revisionScanItems}
      </DropdownButton>
    );
  }

  return scanLabel;
};
export default ProjectSummaryV2IssueScanDropdown;
